import { create } from 'zustand';
import { ChestState } from '../types/chest.types';
import { itemOrderRank } from '../data/inventory.config';

const CHEST_STORAGE_KEY = 'usagi_chest';

function loadChest(): ChestState {
  try {
    const raw = localStorage.getItem(CHEST_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ChestState>;
      return {
        slots: Array.isArray(parsed.slots) ? parsed.slots : [],
        gold:
          typeof parsed.gold === 'number' && parsed.gold > 0 ? parsed.gold : 0,
      };
    }
  } catch {
    /* ignore */
  }
  return { slots: [], gold: 0 };
}

interface ChestActions {
  addItem: (itemId: string, quantity: number) => void;
  removeItem: (itemId: string, quantity: number) => boolean;
  getQuantity: (itemId: string) => number;
  /** Add gold to the chest stash (no cap). */
  addGold: (amount: number) => void;
  /** Remove gold. Returns false (no mutation) if the balance is insufficient. */
  removeGold: (amount: number) => boolean;
  sortByType: () => void;
  persist: () => void;
}

export const useChestStore = create<ChestState & ChestActions>((set, get) => ({
  ...loadChest(),

  addItem: (itemId, quantity) => {
    set((s) => {
      const existing = s.slots.find((sl) => sl.itemId === itemId);
      if (existing) {
        return {
          slots: s.slots.map((sl) =>
            sl.itemId === itemId
              ? { ...sl, quantity: sl.quantity + quantity }
              : sl,
          ),
        };
      }
      return { slots: [...s.slots, { itemId, quantity }] };
    });
    get().persist();
  },

  removeItem: (itemId, quantity) => {
    const existing = get().slots.find((sl) => sl.itemId === itemId);
    if (!existing || existing.quantity < quantity) {
      return false;
    }

    set((s) => ({
      slots: s.slots
        .map((sl) =>
          sl.itemId === itemId
            ? { ...sl, quantity: sl.quantity - quantity }
            : sl,
        )
        .filter((sl) => sl.quantity > 0),
    }));
    get().persist();
    return true;
  },

  getQuantity: (itemId) => {
    const slot = get().slots.find((sl) => sl.itemId === itemId);
    return slot?.quantity ?? 0;
  },

  addGold: (amount) => {
    if (amount <= 0) {
      return;
    }
    set((s) => ({ gold: s.gold + amount }));
    get().persist();
  },

  removeGold: (amount) => {
    if (amount <= 0) {
      return true;
    }
    if (get().gold < amount) {
      return false;
    }
    set((s) => ({ gold: s.gold - amount }));
    get().persist();
    return true;
  },

  sortByType: () => {
    set((s) => ({
      slots: [...s.slots].sort(
        (a, b) => itemOrderRank(a.itemId) - itemOrderRank(b.itemId),
      ),
    }));
    get().persist();
  },

  persist: () => {
    const { slots, gold } = get();
    localStorage.setItem(CHEST_STORAGE_KEY, JSON.stringify({ slots, gold }));
  },
}));
